import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Scan, Package, AlertTriangle, Image as ImgIcon, Plus, Minus, ChevronRight } from "lucide-react";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { inventoryApi } from "../lib/shop-api";
import { money } from "../lib/api";

export default function ScanProduct() {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [product, setProduct] = useState(null);
  const [barcode, setBarcode] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const lookup = async (code) => {
    setBusy(true);
    setBarcode(code);
    try {
      const p = await inventoryApi.byBarcode(code);
      setProduct(p);
      setNotFound(false);
      toast.success(`Encontrado: ${p.name}`);
    } catch (err) {
      if (err.status === 404) {
        setProduct(null);
        setNotFound(true);
      } else {
        toast.error(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const onScanned = async (code) => {
    setScannerOpen(false);
    await lookup(code);
  };

  const adjust = async (variantId, delta) => {
    try {
      const updated = await inventoryApi.adjustStock(product.id, {
        variant_id: variantId,
        delta,
        reason: delta > 0 ? "entrada manual (scan)" : "salida manual (scan)",
      });
      setProduct(updated);
      toast.success("Inventario actualizado");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-full">
      <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#C89B3C] mb-2">Inventario</p>
        <h1 className="text-4xl md:text-5xl text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }}>Escanear Producto</h1>
      </div>

      {!product && !notFound && (
        <div className="bg-[#120F0D] border border-[#C89B3C]/30 rounded-2xl p-10 text-center">
          <div className="w-24 h-24 bg-[#C89B3C]/15 rounded-full flex items-center justify-center mx-auto mb-6">
            <Scan size={44} className="text-[#C89B3C]" />
          </div>
          <h2 className="text-2xl mb-2 text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'" }}>Apunta el código y listo</h2>
          <p className="text-sm text-[#F5F0E6]/60 mb-6">Botas, sombreros, camisas: escanea UPC/EAN y ve stock por talla al instante.</p>
          <button
            onClick={() => setScannerOpen(true)}
            className="inline-flex items-center gap-3 bg-[#C89B3C] hover:bg-[#E6BF5F] text-[#0A0908] font-black uppercase tracking-wider text-lg px-8 py-5 rounded-full shadow-xl shadow-[#C89B3C]/20"
            data-testid="scan-main-btn"
          >
            <Scan size={22} /> ESCANEAR PRODUCTO
          </button>
          <p className="text-xs text-[#F5F0E6]/40 mt-6">También puedes escribir el código manualmente al abrir el escáner.</p>
        </div>
      )}

      {busy && <p className="text-[#F5F0E6]/60">Buscando…</p>}

      {notFound && (
        <div className="bg-[#120F0D] border border-amber-500/40 rounded-2xl p-6" data-testid="scan-not-found">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="text-amber-400" size={22} />
            <div>
              <p className="text-sm text-amber-300 uppercase tracking-wider">No se encontró</p>
              <p className="text-xl text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'" }}>
                Código <span className="font-mono text-[#C89B3C]">{barcode}</span>
              </p>
            </div>
          </div>
          <p className="text-sm text-[#F5F0E6]/70 mb-5">Este producto todavía no está en el sistema. Créalo y asigna este código.</p>
          <div className="flex gap-3">
            <button onClick={() => nav(`/admin/products/new?barcode=${encodeURIComponent(barcode)}`)} className="bg-[#C89B3C] hover:bg-[#E6BF5F] text-[#0A0908] font-bold uppercase tracking-wider px-5 py-3 rounded-full" data-testid="scan-create-new-btn">
              Crear Producto Nuevo
            </button>
            <button onClick={() => { setNotFound(false); setScannerOpen(true); }} className="bg-white/5 border border-white/15 text-[#F5F0E6] px-5 py-3 rounded-full">
              Reintentar escaneo
            </button>
          </div>
        </div>
      )}

      {product && (
        <div className="bg-[#120F0D] border border-white/10 rounded-2xl overflow-hidden" data-testid="scan-result">
          <div className="grid md:grid-cols-[220px_1fr]">
            <div className="aspect-square md:aspect-auto bg-black/40">
              {(product.images || [])[0]
                ? <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-white/20"><ImgIcon size={60} /></div>}
            </div>
            <div className="p-6">
              <p className="text-[11px] uppercase tracking-[0.4em] text-[#C89B3C] mb-1">{product.category}{product.brand ? ` · ${product.brand}` : ""}</p>
              <h2 className="text-3xl text-[#F5F0E6] mb-3" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }} data-testid="scan-product-name">{product.name}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><p className="text-[#F5F0E6]/50 uppercase">SKU</p><p className="text-[#F5F0E6] font-mono mt-0.5">{product.sku || "—"}</p></div>
                <div><p className="text-[#F5F0E6]/50 uppercase">Barcode</p><p className="text-[#F5F0E6] font-mono mt-0.5">{product.barcode || "—"}</p></div>
                <div><p className="text-[#F5F0E6]/50 uppercase">Precio</p><p className="text-[#C89B3C] font-bold mt-0.5">{money(product.price)}</p></div>
                <div><p className="text-[#F5F0E6]/50 uppercase">Total stock</p><p className={`font-bold mt-0.5 ${product.total_stock === 0 ? "text-red-400" : product.total_stock <= (product.low_stock_threshold || 5) ? "text-amber-400" : "text-green-400"}`} data-testid="scan-total-stock">{product.total_stock}</p></div>
              </div>
              {product.total_stock <= (product.low_stock_threshold || 5) && product.total_stock > 0 && (
                <p className="mt-3 text-xs flex items-center gap-1 text-amber-400" data-testid="scan-low-stock-warning"><AlertTriangle size={14} /> Low stock</p>
              )}
              {product.total_stock === 0 && (
                <p className="mt-3 text-xs flex items-center gap-1 text-red-400"><AlertTriangle size={14} /> Agotado</p>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-white/10">
            <h3 className="text-lg mb-3 text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'" }}>Stock por variante</h3>
            {product.variants && product.variants.length > 0 ? (
              <ul className="divide-y divide-white/10" data-testid="scan-variants">
                {product.variants.map((v) => (
                  <li key={v.id} className="py-3 flex items-center gap-4" data-testid={`scan-variant-${v.id}`}>
                    <div className="flex-1">
                      <p className="text-[#F5F0E6] font-semibold">
                        {v.size || "—"}{v.color ? ` · ${v.color}` : ""}
                      </p>
                      <p className="text-xs text-[#F5F0E6]/50 font-mono">{v.barcode || v.sku || ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => adjust(v.id, -1)} className="w-10 h-10 bg-white/5 border border-white/15 rounded-full flex items-center justify-center hover:border-red-400 hover:text-red-400" data-testid={`scan-dec-${v.id}`}><Minus size={16} /></button>
                      <span className={`text-xl font-bold min-w-[48px] text-center ${v.quantity === 0 ? "text-red-400" : v.quantity <= 3 ? "text-amber-400" : "text-[#F5F0E6]"}`} data-testid={`scan-qty-${v.id}`}>{v.quantity}</span>
                      <button onClick={() => adjust(v.id, 1)} className="w-10 h-10 bg-white/5 border border-white/15 rounded-full flex items-center justify-center hover:border-green-400 hover:text-green-400" data-testid={`scan-inc-${v.id}`}><Plus size={16} /></button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="flex items-center justify-between py-3" data-testid="scan-simple-stock">
                <p className="text-[#F5F0E6]/70">Producto sin variantes</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => adjust(null, -1)} className="w-10 h-10 bg-white/5 border border-white/15 rounded-full flex items-center justify-center hover:border-red-400 hover:text-red-400"><Minus size={16} /></button>
                  <span className="text-xl font-bold min-w-[48px] text-center text-[#F5F0E6]">{product.quantity_simple || 0}</span>
                  <button onClick={() => adjust(null, 1)} className="w-10 h-10 bg-white/5 border border-white/15 rounded-full flex items-center justify-center hover:border-green-400 hover:text-green-400"><Plus size={16} /></button>
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => setScannerOpen(true)} className="bg-[#C89B3C] hover:bg-[#E6BF5F] text-[#0A0908] font-bold uppercase tracking-wider px-5 py-3 rounded-full flex items-center gap-2" data-testid="scan-another-btn">
                <Scan size={16} /> Escanear otro
              </button>
              <button onClick={() => nav(`/admin/products/${product.id}`)} className="bg-white/5 border border-white/15 text-[#F5F0E6] px-5 py-3 rounded-full flex items-center gap-1" data-testid="scan-edit-btn">
                Editar producto <ChevronRight size={16} />
              </button>
              <button onClick={() => { setProduct(null); setNotFound(false); setBarcode(""); }} className="text-sm text-[#F5F0E6]/60 hover:text-[#C89B3C] px-5 py-3">
                Nuevo escaneo
              </button>
            </div>
          </div>
        </div>
      )}

      {scannerOpen && (
        <BarcodeScanner onDetected={onScanned} onClose={() => setScannerOpen(false)} title="Escanear Producto" />
      )}
    </div>
  );
}
